<script setup lang="ts">
    import { useTheme } from 'vuetify';
    import { BetSelection, EventFeedType, Jollywhere } from '@/constants';
    import { useAppStore } from '@/stores/app';
    import { getTeamLogoUrl } from '@/helpers';

    const {
        id = '0',
        odds,
        isConfirm = false,
    } = defineProps<{
        id: string;
        odds: string;
        isConfirm?: boolean;
    }>();

    const theme = useTheme();
    const appStore = useAppStore();
    const { t } = useI18n();

    const betInfoLine = computed(() => {
        let betInfoLine = '';
        let teamClass = '';

        const bettingData = appStore.betFeedData[id];

        if (!bettingData) return '';

        const selectionId = bettingData.betSelectionId;
        const handicap = bettingData.handicap;
        const jollywhere = bettingData.jollywhere;

        const getTeamNameByBetSelection = (betSelectionId: number): string => {
            switch (betSelectionId) {
                case BetSelection.HandicapAway:
                case BetSelection.OneXTwoAway:
                case BetSelection.CornersHandicapAway:
                    return appStore.betFeedData[id].teamAwayName;
                default:
                    return appStore.betFeedData[id].teamHomeName;
            }
        };

        const getTeamLogoByBetSelection = (betSelectionId: number): string => {
            switch (betSelectionId) {
                case BetSelection.HandicapAway:
                case BetSelection.OneXTwoAway:
                case BetSelection.CornersHandicapAway:
                    if (appStore.betFeedData[id].teamAwayLogo) {
                        const logoUrl = getTeamLogoUrl(appStore.betFeedData[id].teamAwayLogo);
                        return `<img src="${logoUrl}" class="team-logo-betting" alt="team away" />`;
                    }

                    return '<img src="https://cdn.sportmonks.com/images/soccer/team_placeholder.png" class="team-logo-betting" alt="team away" />';
                default:
                    if (appStore.betFeedData[id].teamHomeLogo) {
                        const logoUrl = getTeamLogoUrl(appStore.betFeedData[id].teamHomeLogo);
                        return `<img src="${logoUrl}" class="team-logo-betting" alt="team home" />`;
                    }

                    return '<img src="https://cdn.sportmonks.com/images/soccer/team_placeholder.png" class="team-logo-betting" alt="team home" />';
            }
        };

        const teamName = getTeamNameByBetSelection(selectionId);
        const teamLogo = getTeamLogoByBetSelection(selectionId);

        const confirmClass = isConfirm ? 'bet-dialog-confirm' : '';

        const createLine = (teamClass: string, team: string, teamLogo: string, handicap: string | null = null) =>
            `<span class="bet-dialog-info-line ${confirmClass} ${teamClass} ${handicap !== null ? 'handicap' : ''}">${teamLogo} ${team}&nbsp;</span>${handicap !== null ? handicap + '&nbsp;' : ''}`;

        const handicapNumber = parseFloat(handicap || '0');

        if (selectionId === BetSelection.OneXTwoHome || selectionId === BetSelection.OneXTwoAway) {
            const isHome = selectionId === BetSelection.OneXTwoHome;
            teamClass = handicapNumber === 0 ? 'dogTeam' : jollywhere === (isHome ? Jollywhere.Home : Jollywhere.Away) ? 'jollywhereTeam' : 'dogTeam';
            betInfoLine = createLine(teamClass, teamName, teamLogo);
        } else if (selectionId === BetSelection.GoalOver) {
            betInfoLine = `${t('betDialog.over')} ${handicap}&nbsp;`;
        } else if (selectionId === BetSelection.GoalUnder) {
            betInfoLine = `${t('betDialog.under')} ${handicap}&nbsp;`;
        } else if (selectionId === BetSelection.CornersGoalOver) {
            betInfoLine = `${t('betDialog.cornersOver')} ${handicap}&nbsp;`;
        } else if (selectionId === BetSelection.CornersGoalUnder) {
            betInfoLine = `${t('betDialog.cornersUnder')} ${handicap}&nbsp;`;
        } else if (selectionId === BetSelection.OneXTwoDraw) {
            betInfoLine = `${t('betDialog.draw')}&nbsp;`;
        } else if (selectionId === BetSelection.HandicapHome || selectionId === BetSelection.CornersHandicapHome) {
            const sign = handicapNumber === 0 ? handicap : (jollywhere === Jollywhere.Home ? '-' : '+') + handicap;
            teamClass = handicapNumber === 0 ? 'dogTeam' : jollywhere === Jollywhere.Home ? 'jollywhereTeam' : 'dogTeam';
            betInfoLine = createLine(teamClass, teamName, teamLogo, sign);
        } else if (selectionId === BetSelection.HandicapAway || selectionId === BetSelection.CornersHandicapAway) {
            const sign = handicapNumber === 0 ? handicap : (jollywhere === Jollywhere.Away ? '-' : '+') + handicap;
            teamClass = handicapNumber === 0 ? 'dogTeam' : jollywhere === Jollywhere.Away ? 'jollywhereTeam' : 'dogTeam';
            betInfoLine = createLine(teamClass, teamName, teamLogo, sign);
        } else if (selectionId === BetSelection.BtsYes) {
            betInfoLine = `${t('betDialog.btts')} - ${t('betDialog.yes')}&nbsp;`;
        } else if (selectionId === BetSelection.BtsNo) {
            betInfoLine = `${t('betDialog.btts')} - ${t('betDialog.no')}&nbsp;`;
        } else if (selectionId === BetSelection.Dc1X) {
            betInfoLine = `${t('betDialog.home')}${t('betDialog.or')}${t('betDialog.draw')}&nbsp;`;
        } else if (selectionId === BetSelection.Dc12) {
            betInfoLine = `${t('betDialog.home')}${t('betDialog.or')}${t('betDialog.away')}&nbsp;`;
        } else if (selectionId === BetSelection.DcX2) {
            betInfoLine = `${t('betDialog.draw')}${t('betDialog.or')}${t('betDialog.away')}&nbsp;`;
        } else if (selectionId === BetSelection.Cs) {
            betInfoLine = `${t('betDialog.correctScore')} ${bettingData.correctScore}&nbsp;`;
        }

        if (
            bettingData.eventFeedType === EventFeedType.Live &&
            [BetSelection.CornersHandicapHome, BetSelection.CornersHandicapAway, BetSelection.CornersGoalOver, BetSelection.CornersGoalUnder].includes(selectionId)
        ) {
            betInfoLine += `(${bettingData.corners || t('bet.NC')})&nbsp;`;
        } else if (bettingData.eventFeedType === EventFeedType.Live) {
            betInfoLine += `(${bettingData.scoreFull || t('bet.NC')})&nbsp;`;
        }

        betInfoLine += `<span class="odds-info">@${odds ? parseFloat(odds).toFixed(3) : ''}</span>`;
        return betInfoLine;
    });
</script>

<template>
    <v-chip :color="theme.name.value === 'dark' ? '#323338' : '#e3e3e3'" class="info-line info-chip" label variant="flat">
        <span class="bet-info-team" v-html="betInfoLine"></span>
    </v-chip>
</template>

<style scoped>
    .info-line {
        font-weight: 500;
    }

    .info-chip {
        padding: 0 5px;
        font-size: 16.25px;
        height: 32px;
        margin-top: 6px;
    }

    .v-theme--light .info-line.v-chip {
        background-color: #f7f7f7 !important;
        border-color: #e3e3e3 !important;
        border-width: 1px !important;
        border-style: solid !important;
    }

    .v-theme--light .info-line.v-chip:before {
        background-color: #f7f7f7;
    }

    .v-theme--dark .info-line.v-chip {
        background-color: #323338 !important;
    }

    .v-theme--dark .info-line.v-chip:before {
        background-color: #323338;
    }
</style>
